import React, { useCallback, useEffect, useState } from 'react';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { SkeletonBlock, EmptyState } from '../../components/module-ui';
import HelpPagination, { usePaged } from './HelpPagination';
import {
  approveTicketSuggestion, closeUnanswered, listTicketSuggestions, listUnanswered,
  rejectTicketSuggestion, scanTicketSuggestions,
} from './helpApi';

const cardStyle = { background: 'white', border: '1px solid #E5E7EB', padding: '12px' };
const mutedText = { fontSize: '11px', color: '#6B7280' };

const REASON_LABELS = {
  no_match: 'No answer found',
  unclear: 'Unclear — user was shown choices',
  not_helpful: 'Marked not helpful',
};

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

/** Questions users asked that had no confident answer. */
export function UnansweredPanel({ onAnswer }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const paged = usePaged(items, 'unanswered');

  const load = useCallback(async () => {
    try {
      setItems(await listUnanswered());
    } catch (err) {
      console.error('Failed to load unanswered questions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const dismiss = async (item) => {
    try {
      await closeUnanswered(item.unanswered_id);
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to dismiss');
    }
  };

  if (loading) return <SkeletonBlock />;
  if (!items.length) {
    return (
      <EmptyState
        icon={MagnifyingGlass}
        title="Nothing unanswered"
        description="Questions users ask that Help cannot answer will appear here."
      />
    );
  }

  return (
    <div style={{ display: 'grid', gap: '8px' }} data-testid="help-unanswered">
      {paged.pageItems.map((item) => (
        <div key={item.unanswered_id} style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'flex-start' }}>
            <div>
              <p style={{ fontSize: '13px', fontWeight: '600', margin: 0 }}>{item.question}</p>
              <p style={{ ...mutedText, marginTop: '4px' }}>
                {REASON_LABELS[item.reason] || item.reason}
                {item.entry_title ? ` (answer shown: "${item.entry_title}")` : ''}
                {' · '}asked {item.count} time{item.count === 1 ? '' : 's'}
                {item.roles?.length ? ` · by ${item.roles.join(', ')}` : ''}
                {' · '}last {formatDate(item.last_asked_at)}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
              <button
                type="button"
                className="btn-primary"
                style={{ fontSize: '12px' }}
                onClick={() => onAnswer(item, async () => {
                  await closeUnanswered(item.unanswered_id);
                  load();
                })}
              >
                Answer
              </button>
              <button type="button" className="btn-secondary" style={{ fontSize: '12px' }} onClick={() => dismiss(item)}>
                Dismiss
              </button>
            </div>
          </div>
        </div>
      ))}
      <HelpPagination {...paged} />
    </div>
  );
}

function SuggestionCard({ suggestion, onDone }) {
  const [question, setQuestion] = useState(suggestion.question);
  const [answer, setAnswer] = useState(suggestion.answer);
  const [category, setCategory] = useState(suggestion.category || 'General');
  const [busy, setBusy] = useState(false);

  const approve = async () => {
    setBusy(true);
    try {
      await approveTicketSuggestion(suggestion.suggestion_id, { question, answer, category, tags: [] });
      onDone(true);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to approve');
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await rejectTicketSuggestion(suggestion.suggestion_id);
      onDone(false);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to reject');
      setBusy(false);
    }
  };

  return (
    <div style={cardStyle}>
      <p style={{ ...mutedText, marginBottom: '6px' }}>From ticket {suggestion.ticket_no} — edit before approving; remove any client-specific details.</p>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Question</label>
      <input className="input-field" value={question} onChange={(e) => setQuestion(e.target.value)} style={{ marginBottom: '8px' }} />
      <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Answer</label>
      <textarea className="input-field" rows={4} value={answer} onChange={(e) => setAnswer(e.target.value)} style={{ marginBottom: '8px' }} />
      <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Category</label>
      <input className="input-field" value={category} onChange={(e) => setCategory(e.target.value)} style={{ marginBottom: '8px' }} />
      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
        <button type="button" className="btn-secondary" disabled={busy} onClick={reject}>Reject</button>
        <button type="button" className="btn-primary" disabled={busy || question.trim().length < 3 || !answer.trim()} onClick={approve}>
          Approve &amp; publish
        </button>
      </div>
    </div>
  );
}

/** Resolution notes of resolved tickets, offered as answers for admin review. */
export function TicketSuggestionsPanel({ onApproved }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [message, setMessage] = useState('');
  const paged = usePaged(items, 'suggestions');

  const load = useCallback(async () => {
    try {
      setItems(await listTicketSuggestions());
    } catch (err) {
      console.error('Failed to load ticket suggestions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const scan = async () => {
    setScanning(true);
    setMessage('');
    try {
      const res = await scanTicketSuggestions();
      if (!res.added) setMessage('No new resolved tickets to suggest.');
      else if (res.added >= res.batch_limit) setMessage(`${res.added} new suggestions added (newest first). Review them, then scan again for more.`);
      else setMessage(`${res.added} new suggestion${res.added === 1 ? '' : 's'} found.`);
      load();
    } catch (err) {
      setMessage(err?.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleDone = (approved) => {
    load();
    if (approved) onApproved();
  };

  return (
    <div data-testid="help-ticket-suggestions">
      <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '12px' }}>
        <p style={{ fontSize: '12px', color: '#374151', margin: 0 }}>
          Turn the resolution notes of completed and closed tickets into help answers. Nothing is shown to users until you approve it.
        </p>
        <button type="button" className="btn-primary" onClick={scan} disabled={scanning} style={{ whiteSpace: 'nowrap' }}>
          {scanning ? 'Scanning…' : 'Scan resolved tickets'}
        </button>
      </div>
      {message && <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '8px' }}>{message}</p>}
      {loading ? (
        <SkeletonBlock />
      ) : items.length === 0 ? (
        <EmptyState
          icon={MagnifyingGlass}
          title="No suggestions waiting"
          description="Click Scan resolved tickets to look for new ones."
        />
      ) : (
        <>
          <div style={{ display: 'grid', gap: '8px' }}>
            {paged.pageItems.map((s) => (
              <SuggestionCard key={s.suggestion_id} suggestion={s} onDone={handleDone} />
            ))}
          </div>
          <HelpPagination {...paged} />
        </>
      )}
    </div>
  );
}
